import type { ReactNode } from 'react';
import { onCheckboxKey } from './checkboxKey';
import styles from './ExportSelectable.module.css';

/** What one transcript item needs to take part in the PDF export. */
export interface IExportSelectionProps {
  exportMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  exportRef?: (el: HTMLDivElement | null) => void;
}

interface IExportSelectableProps extends IExportSelectionProps {
  children: ReactNode;
}

// The same path a pointer takes, so the keyboard cannot select differently.
const clickOnKey = onCheckboxKey((frame) => frame.click());

/**
 * The frame that makes one drawing — a table, a chart — selectable for the PDF
 * export, and the element the export captures. (Prose is picked by passage
 * instead: `ExportableProse`.)
 *
 * In export mode the whole item is one checkbox: a click anywhere on it, or
 * Space/Enter while it has focus, selects it, and its contents stop taking
 * pointer events so that click cannot land on a sort header or the chart-type
 * switcher underneath. A `div` with the checkbox role rather than a button,
 * because a button may not hold the table's own interactive markup. Out of
 * export mode it is a plain div, so the ref the export captures from stays
 * mounted.
 *
 * One frame for every drawing, because a copy per kind is how one of them
 * quietly stops being selectable.
 */
export function ExportSelectable(props: IExportSelectableProps) {
  const { exportMode, isSelected, onToggleSelect, exportRef, children } = props;
  if (!exportMode) return <div ref={exportRef}>{children}</div>;

  return (
    <div
      ref={exportRef}
      role="checkbox"
      aria-checked={Boolean(isSelected)}
      tabIndex={0}
      className={`${styles.selectable} ${isSelected ? styles.selected : ''}`}
      onClick={onToggleSelect}
      onKeyDown={clickOnKey}
    >
      {children}
    </div>
  );
}
